import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Tooltip from 'apollo-react/components/Tooltip';
import ClipboardSearch from 'apollo-react-icons/ClipboardSearch';
import qs from 'qs';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { compose } from 'redux';

import SearchInput from './EnhancedSearchInput';
import EntitySelect from './EntitySelect';
import {
  fetchEnhancedSearchResultsPage,
  makeEnhancedSearchAdvanced,
  resetSearchResults,
} from '../../actions/SearchPageActions';
import {
  getEnhancedSearchTreeViewData,
  resetSearchTreeViewData,
} from '../../actions/SearchTreeViewDataActions';
import { fetchMenuOptions, setSearchText } from '../../actions/TopMenuActions';
import { ENHANCED_SEARCH_PAGE } from '../../constants/Urls';
import { getAppOptionByKey } from '../../selectors/AppConfigsSelectors';
import withTranslation from '../withTranslation';

const useStyles = makeStyles({
  container: {
    height: 44,
    backgroundColor: 'white',
    borderRadius: 4,
  },
  select: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0 8px',
    position: 'relative',
  },
});

const SearchBar = ({ translateString }) => {
  const classes = useStyles();
  const domRef = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const options = useSelector((state) => state.topMenu.searchOptions ?? []);
  const searchPage = useSelector((state) => state?.searchPage);
  const searchText = useSelector((state) => state.topMenu.searchText);
  const allowEnhancedGlobalSearch = useSelector(getAppOptionByKey('allowEnhancedGlobalSearch'));
  const fetchedOptions = useSelector((state) => !!state.topMenu.fetchedOptions);
  const [dropDownWidth, setDropDownWidth] = useState(null);
  const [entitySelected, setEntitySelected] = useState('ALL');
  const history = useHistory();

  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const handleEntityTypeChange = ({ target: { value } }) => {
    dispatch(resetSearchTreeViewData());
    dispatch(
      makeEnhancedSearchAdvanced({
        isAdvancedSearch: false,
        entityNames: value !== 'ALL' && value !== '' ? [value] : [],
        searchText: '',
        getCrossReferences: false,
        searchOption: 0,
        advancedSearchCriteria: [],
        page: 0,
        useContainsTable: false,
      })
    );
    setEntitySelected(value);
    dispatch(getEnhancedSearchTreeViewData(value));
  };

  const handleSearch = () => {
    if (searchText && searchText?.trim() !== '') {
      dispatch(resetSearchResults());
      dispatch(
        makeEnhancedSearchAdvanced({
          isAdvancedSearch: false,
          entityNames: entitySelected !== 'ALL' && entitySelected !== '' ? [entitySelected] : [],
          useContainsTable: false,
          searchOption: 0,
        })
      );
      dispatch(
        fetchEnhancedSearchResultsPage({
          searchText,
          entityNames: entitySelected !== 'ALL' && entitySelected !== '' ? [entitySelected] : [],
          advancedSearchCriteria: searchPage?.advancedSearchCriteria,
          useContainsTable: false,
          searchOption: 0,
        })
      );
      history.push(
        `${ENHANCED_SEARCH_PAGE}?searchQuery=${
          searchText ? searchText : ''
        }&entityType=${entitySelected}`
      );
    }
  };

  const handleAdvancedSearchClick = () => {
    dispatch(
      makeEnhancedSearchAdvanced({
        isAdvancedSearch: true,
        searchText,
        entityNames: entitySelected !== 'ALL' && entitySelected !== '' ? [entitySelected] : [],
        advancedSearchCriteria: searchPage?.advancedSearchCriteria,
        useContainsTable: false,
        searchOption: 0,
      })
    );
    history.push(
      `${ENHANCED_SEARCH_PAGE}?searchQuery=${
        searchText ? searchText : ''
      }&entityType=${entitySelected}`
    );
  };

  useEffect(() => {
    dispatch(fetchMenuOptions());
    setDropDownWidth(domRef.current.offsetWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location?.pathname === '/' || location?.pathname === '/enhanced-search') {
      if (
        searchParams?.searchQuery &&
        searchParams?.searchQuery?.length &&
        searchParams?.searchQuery !== searchText &&
        !searchPage?.isAdvancedSearch
      ) {
        dispatch(setSearchText(searchParams?.searchQuery));
      } else {
        dispatch(setSearchText(''));
      }

      const entityName =
        searchParams?.entityType &&
        searchParams?.entityType !== 'All' &&
        searchParams?.entityType !== ''
          ? searchParams?.entityType
          : 'ALL';

      setEntitySelected(entityName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    setDropDownWidth(domRef.current.offsetWidth);
  }, [options, entitySelected, searchText]);

  return (
    <>
      <div ref={domRef}>
        <Grid className={classes.container} container alignItems="stretch">
          <Grid item>
            <SearchInput
              onSearch={handleSearch}
              enableSearch={searchText !== ''}
              dropDownWidth={dropDownWidth}
              entitySelected={entitySelected}
            />
          </Grid>
          <Grid className={classes.select} item>
            <EntitySelect
              placeholder={
                fetchedOptions ? translateString('Select') : `${translateString('Loading')}...`
              }
              value={entitySelected}
              onChange={handleEntityTypeChange}
              anchorEl={domRef.current}
              anchorWidth={dropDownWidth}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  <Tooltip title={option.text} placement="bottom">
                    <span>{option.text}</span>
                  </Tooltip>
                </MenuItem>
              ))}
            </EntitySelect>
          </Grid>
          {allowEnhancedGlobalSearch && (
            <Grid>
              <Tooltip title={translateString('Advanced Options')} placement="bottom">
                <IconButton
                  onClick={handleAdvancedSearchClick}
                  aria-label={translateString('Advanced Options')}
                >
                  <ClipboardSearch color="primary" />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  );
};

const EnhancedComponent = compose(withRouter, withTranslation)(SearchBar);

export default EnhancedComponent;
